import type { IndexedMesh } from "./geometry.ts";

/** A per-vertex attribute carried through remeshing (interpolated for new vertices). */
export interface Channel {
  data: Float32Array;
  stride: number;
  /**
   * A copy of the positions (e.g. the displayed surface): gets the same curvature offset
   * as the positions when an edge is split, and the same slide when vertices are relaxed.
   */
  follows?: boolean;
}

export interface RemeshOptions {
  /** Only edges touching these vertices are edited (default: all). */
  region?: Uint8Array;
  /**
   * Do nothing unless some edge in the region is longer than target × trigger or shorter
   * than target ÷ trigger (default 4/3, the split threshold). A larger value lets small
   * deviations wait.
   */
  trigger?: number;
}

export interface RemeshResult extends IndexedMesh {
  channels: Float32Array[];
}

/** Target valence of an interior vertex of a regular triangle mesh. */
const VALENCE = 6;
/** Faces around a collapse may turn by at most this much (cosine) — no folding. */
const MIN_COLLAPSE_COS = 0.2;
/** Only nearly flat pairs of triangles are flipped, so flips do not change the shape. */
const MIN_FLIP_COS = 0.9;
/** Tangential relaxation after the topological changes. */
const RELAX_ITERATIONS = 2;
const RELAX_STEP = 0.5;

/**
 * Isotropic remeshing after Botsch & Kobbelt: split edges longer than 4/3 of `target`,
 * collapse edges shorter than 4/5 of it, flip edges toward valence 6 and relax
 * tangentially. Only local operations on a closed manifold are used, and collapses that
 * would pinch the surface are refused, so the number of holes never changes.
 * Returns null when nothing needed changing.
 */
export function remesh(
  positions: Float32Array,
  index: Uint32Array,
  channels: Channel[],
  target: number,
  { region, trigger = 4 / 3 }: RemeshOptions = {},
): RemeshResult | null {
  const inRegion = (v: number) => !region || region[v] > 0;
  const long2 = (target * trigger) ** 2, short2 = (target / trigger) ** 2;
  let needed = false;
  for (let t = 0; t < index.length && !needed; t += 3) {
    for (let e = 0; e < 3; e++) {
      const a = index[t + e], b = index[t + (e + 1) % 3];
      if (!inRegion(a) && !inRegion(b)) continue;
      const d2 = dist2(positions, a, b);
      if (d2 > long2 || d2 < short2) {
        needed = true;
        break;
      }
    }
  }
  if (!needed) return null;

  const high2 = (target * 4 / 3) ** 2, low2 = (target * 4 / 5) ** 2;
  const m = new Editor(positions, index, channels, region);
  for (let pass = 0; pass < 4; pass++) {
    let splits = 0;
    const edges = m.edges();
    for (let k = 0; k < edges.length; k += 2) {
      const a = edges[k], b = edges[k + 1];
      if (m.dist2(a, b) > high2 && m.split(a, b)) splits++;
    }
    if (splits === 0) break;
  }
  let edges = m.edges();
  for (let k = 0; k < edges.length; k += 2) {
    const a = edges[k], b = edges[k + 1];
    if (m.alive(a) && m.alive(b) && m.dist2(a, b) < low2) m.collapse(a, b, high2);
  }
  edges = m.edges();
  for (let k = 0; k < edges.length; k += 2) m.flip(edges[k], edges[k + 1]);
  m.relax();
  return m.compact();
}

function dist2(p: ArrayLike<number>, a: number, b: number): number {
  const dx = p[a * 3] - p[b * 3],
    dy = p[a * 3 + 1] - p[b * 3 + 1],
    dz = p[a * 3 + 2] - p[b * 3 + 2];
  return dx * dx + dy * dy + dz * dz;
}

/** Growable Float32Array. */
class Floats {
  data: Float32Array;
  length: number;

  constructor(source: Float32Array) {
    this.data = new Float32Array(Math.ceil(source.length * 1.5) + 64);
    this.data.set(source);
    this.length = source.length;
  }

  push(value: number): void {
    if (this.length === this.data.length) {
      const grown = new Float32Array(this.data.length * 2);
      grown.set(this.data);
      this.data = grown;
    }
    this.data[this.length++] = value;
  }
}

/** Growable Int32Array. */
class Ints {
  data: Int32Array;
  length: number;

  constructor(source: ArrayLike<number>) {
    this.data = new Int32Array(Math.ceil(source.length * 1.5) + 64);
    this.data.set(source);
    this.length = source.length;
  }

  push(value: number): void {
    if (this.length === this.data.length) {
      const grown = new Int32Array(this.data.length * 2);
      grown.set(this.data);
      this.data = grown;
    }
    this.data[this.length++] = value;
  }
}

/**
 * Growable indexed triangle mesh for local edits. Work is proportional to the region
 * being edited: vertex → face lists are only materialized for vertices that are visited.
 */
class Editor {
  private readonly pos: Floats;
  private readonly chans: { buf: Floats; stride: number; follows: boolean }[];
  /** Three vertex ids per face; -1 once removed. */
  private readonly faces: Ints;
  /** Faces around each original vertex (CSR), the source for materialized lists. */
  private readonly incidenceStart: Uint32Array;
  private readonly incidence: Uint32Array;
  /** Mutable face lists, filled in on first use; empty once a vertex is removed. */
  private readonly vfCache: (number[] | undefined)[];
  private readonly originalCount: number;
  /** Editable vertices: the region plus every vertex created by a split. */
  private readonly regionList: number[] = [];
  private readonly inRegion: Ints;
  private readonly ring: number[] = [];
  private readonly ring2: number[] = [];

  constructor(
    positions: Float32Array,
    index: Uint32Array,
    channels: Channel[],
    region?: Uint8Array,
  ) {
    const n = positions.length / 3;
    this.originalCount = n;
    this.pos = new Floats(positions);
    this.chans = channels.map((c) => ({
      buf: new Floats(c.data),
      stride: c.stride,
      follows: c.follows ?? false,
    }));
    this.faces = new Ints(index);
    this.incidenceStart = new Uint32Array(n + 1);
    for (let t = 0; t < index.length; t++) this.incidenceStart[index[t] + 1]++;
    for (let v = 0; v < n; v++) this.incidenceStart[v + 1] += this.incidenceStart[v];
    this.incidence = new Uint32Array(index.length);
    const fill = this.incidenceStart.slice(0, n);
    for (let t = 0; t < index.length; t++) this.incidence[fill[index[t]]++] = Math.floor(t / 3);
    this.vfCache = new Array(n);
    this.inRegion = new Ints(region ?? new Uint8Array(n).fill(1));
    for (let v = 0; v < n; v++) if (this.inRegion.data[v]) this.regionList.push(v);
  }

  alive(v: number): boolean {
    return this.vf(v).length > 0;
  }

  dist2(a: number, b: number): number {
    return dist2(this.pos.data, a, b);
  }

  /** Every current edge of faces around the region once, as flat pairs (a, b) with a < b. */
  edges(): number[] {
    const out: number[] = [];
    const seen = new Set<number>();
    const f = this.faces.data;
    for (const v of this.regionList) {
      for (const face of this.vf(v)) {
        if (seen.has(face)) continue;
        seen.add(face);
        const o = face * 3;
        for (let e = 0; e < 3; e++) {
          const a = f[o + e], b = f[o + (e + 1) % 3];
          if (a < b) out.push(a, b);
        }
      }
    }
    return out;
  }

  /** Splits edge a–b at a point bulged out to follow the surface's curvature. */
  split(a: number, b: number): boolean {
    const fs = this.edgeFaces(a, b);
    if (fs.length !== 2) return false;
    const p = this.pos.data;
    const na = this.vertexNormal(a), nb = this.vertexNormal(b);
    const nx = na[0] + nb[0], ny = na[1] + nb[1], nz = na[2] + nb[2];
    const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    // Sagitta of the arc through a and b with these normals (exact on a sphere).
    const offset = ((p[b * 3] - p[a * 3]) * (nb[0] - na[0]) +
      (p[b * 3 + 1] - p[a * 3 + 1]) * (nb[1] - na[1]) +
      (p[b * 3 + 2] - p[a * 3 + 2]) * (nb[2] - na[2])) / 8;
    const m = this.addVertex(a, b, nx / nl * offset, ny / nl * offset, nz / nl * offset);

    const faces = this.faces;
    for (const f of fs) {
      const o = f * 3;
      let i = 0;
      while (!this.isEdgeAt(f, i, a, b)) i++;
      const x = faces.data[o + i], y = faces.data[o + (i + 1) % 3], z = faces.data[o + (i + 2) % 3];
      faces.data[o] = x;
      faces.data[o + 1] = m;
      faces.data[o + 2] = z;
      const g = faces.length / 3;
      faces.push(m);
      faces.push(y);
      faces.push(z);
      remove(this.vf(y), f);
      this.vf(m).push(f, g);
      this.vf(y).push(g);
      this.vf(z).push(g);
    }
    return true;
  }

  /** Merges b into a at their midpoint, unless that would pinch, fold or stretch the surface. */
  collapse(a: number, b: number, high2: number): boolean {
    const fs = this.edgeFaces(a, b);
    if (fs.length !== 2) return false;
    const c = this.third(fs[0], a, b), d = this.third(fs[1], a, b);
    if (c === d) return false;
    // Link condition: a and b may only share the two opposite vertices.
    const na = this.neighbors(a, this.ring), nb = this.neighbors(b, this.ring2);
    let common = 0;
    for (const w of na) {
      if (!nb.includes(w)) continue;
      if (w !== c && w !== d) return false;
      common++;
    }
    if (common !== 2) return false;
    if (this.vf(c).length <= 3 || this.vf(d).length <= 3) return false;
    if (this.vf(a).length + this.vf(b).length - 4 < 3) return false;

    const p = this.pos.data;
    const mx = (p[a * 3] + p[b * 3]) / 2, my = (p[a * 3 + 1] + p[b * 3 + 1]) / 2;
    const mz = (p[a * 3 + 2] + p[b * 3 + 2]) / 2;
    for (const list of [na, nb]) {
      for (const w of list) {
        if (w === a || w === b) continue;
        const dx = p[w * 3] - mx, dy = p[w * 3 + 1] - my, dz = p[w * 3 + 2] - mz;
        if (dx * dx + dy * dy + dz * dz > high2) return false;
      }
    }
    for (const v of [a, b]) {
      for (const f of this.vf(v)) {
        if (f === fs[0] || f === fs[1]) continue;
        if (!this.keepsFacing(f, a, b, mx, my, mz)) return false;
      }
    }

    this.average(a, b);
    for (const f of fs) this.removeFace(f);
    const faces = this.faces.data;
    const moving = this.vf(b);
    for (const f of moving) {
      const o = f * 3;
      for (let k = 0; k < 3; k++) if (faces[o + k] === b) faces[o + k] = a;
      this.vf(a).push(f);
    }
    moving.length = 0;
    return true;
  }

  /** Flips edge a–b to the other diagonal when that brings valences closer to 6. */
  flip(a: number, b: number): boolean {
    if (!this.alive(a) || !this.alive(b)) return false;
    const fs = this.edgeFaces(a, b);
    if (fs.length !== 2) return false;
    let [f1, f2] = fs;
    if (!this.hasDirected(f1, a, b)) [f1, f2] = [f2, f1];
    const c = this.third(f1, a, b), d = this.third(f2, a, b);
    if (c === d || this.vf(a).length <= 3 || this.vf(b).length <= 3) return false;
    const dev = (v: number, delta: number) => (this.vf(v).length + delta - VALENCE) ** 2;
    const before = dev(a, 0) + dev(b, 0) + dev(c, 0) + dev(d, 0);
    const after = dev(a, -1) + dev(b, -1) + dev(c, 1) + dev(d, 1);
    if (after >= before) return false;
    if (this.neighbors(c, this.ring).includes(d)) return false;

    const n1 = this.triNormal(a, b, c), n2 = this.triNormal(b, a, d);
    const l1 = Math.hypot(n1[0], n1[1], n1[2]), l2 = Math.hypot(n2[0], n2[1], n2[2]);
    if (n1[0] * n2[0] + n1[1] * n2[1] + n1[2] * n2[2] < MIN_FLIP_COS * l1 * l2) return false;
    const sx = n1[0] + n2[0], sy = n1[1] + n2[1], sz = n1[2] + n2[2];
    for (const [x, y, z] of [[a, d, c], [d, b, c]]) {
      const m = this.triNormal(x, y, z);
      if (m[0] * sx + m[1] * sy + m[2] * sz <= 0) return false;
    }

    const faces = this.faces.data;
    faces[f1 * 3] = a;
    faces[f1 * 3 + 1] = d;
    faces[f1 * 3 + 2] = c;
    faces[f2 * 3] = d;
    faces[f2 * 3 + 1] = b;
    faces[f2 * 3 + 2] = c;
    remove(this.vf(b), f1);
    this.vf(d).push(f1);
    remove(this.vf(a), f2);
    this.vf(c).push(f2);
    return true;
  }

  /** Slides region vertices along the surface toward their ring centroid, evening out spacing. */
  relax(): void {
    const p = this.pos.data, moved: number[] = [], rings: number[][] = [];
    for (const v of this.regionList) {
      if (!this.alive(v)) continue;
      moved.push(v);
      rings.push(this.neighbors(v, []));
    }
    const moves = new Float64Array(moved.length * 3);
    for (let it = 0; it < RELAX_ITERATIONS; it++) {
      for (let k = 0; k < moved.length; k++) {
        const v = moved[k], ring = rings[k];
        let x = 0, y = 0, z = 0;
        for (const w of ring) {
          x += p[w * 3];
          y += p[w * 3 + 1];
          z += p[w * 3 + 2];
        }
        x = x / ring.length - p[v * 3];
        y = y / ring.length - p[v * 3 + 1];
        z = z / ring.length - p[v * 3 + 2];
        const [nx, ny, nz] = this.vertexNormal(v);
        const d = x * nx + y * ny + z * nz;
        moves[k * 3] = (x - d * nx) * RELAX_STEP;
        moves[k * 3 + 1] = (y - d * ny) * RELAX_STEP;
        moves[k * 3 + 2] = (z - d * nz) * RELAX_STEP;
      }
      for (let k = 0; k < moved.length; k++) {
        for (let j = 0; j < 3; j++) {
          const i = moved[k] * 3 + j;
          p[i] += moves[k * 3 + j];
          for (const c of this.chans) if (c.follows) c.buf.data[i] += moves[k * 3 + j];
        }
      }
    }
  }

  /** Drops removed vertices and faces and returns compact arrays. */
  compact(): RemeshResult {
    const n = this.pos.length / 3;
    const remap = new Int32Array(n).fill(-1);
    let count = 0;
    for (let v = 0; v < n; v++) {
      const list = this.vfCache[v];
      const alive = list ? list.length > 0 : this.incidenceStart[v + 1] > this.incidenceStart[v];
      if (alive) remap[v] = count++;
    }
    const src = this.pos.data;
    const positions = new Float32Array(count * 3);
    const channels = this.chans.map((c) => new Float32Array(count * c.stride));
    for (let v = 0; v < n; v++) {
      const r = remap[v];
      if (r < 0) continue;
      positions[r * 3] = src[v * 3];
      positions[r * 3 + 1] = src[v * 3 + 1];
      positions[r * 3 + 2] = src[v * 3 + 2];
      for (let ci = 0; ci < channels.length; ci++) {
        const { buf, stride } = this.chans[ci];
        for (let k = 0; k < stride; k++) channels[ci][r * stride + k] = buf.data[v * stride + k];
      }
    }
    const faces = this.faces.data;
    let live = 0;
    for (let t = 0; t < this.faces.length; t += 3) if (faces[t] >= 0) live++;
    const index = new Uint32Array(live * 3);
    let o = 0;
    for (let t = 0; t < this.faces.length; t += 3) {
      if (faces[t] < 0) continue;
      index[o++] = remap[faces[t]];
      index[o++] = remap[faces[t + 1]];
      index[o++] = remap[faces[t + 2]];
    }
    return { positions, index, channels };
  }

  /** Faces around v, materialized from the original incidence on first use. */
  private vf(v: number): number[] {
    let list = this.vfCache[v];
    if (!list) {
      list = v < this.originalCount
        ? Array.from(this.incidence.subarray(this.incidenceStart[v], this.incidenceStart[v + 1]))
        : [];
      this.vfCache[v] = list;
    }
    return list;
  }

  private addVertex(a: number, b: number, bx: number, by: number, bz: number): number {
    const m = this.pos.length / 3;
    const bulge = [bx, by, bz];
    for (let k = 0; k < 3; k++) {
      this.pos.push((this.pos.data[a * 3 + k] + this.pos.data[b * 3 + k]) / 2 + bulge[k]);
    }
    for (const c of this.chans) {
      for (let k = 0; k < c.stride; k++) {
        const v = (c.buf.data[a * c.stride + k] + c.buf.data[b * c.stride + k]) / 2;
        c.buf.push(c.follows ? v + bulge[k] : v);
      }
    }
    this.vfCache[m] = [];
    this.inRegion.push(1);
    this.regionList.push(m);
    return m;
  }

  /** Moves vertex a to the midpoint of a and b (positions and channels). */
  private average(a: number, b: number): void {
    const p = this.pos.data;
    for (let k = 0; k < 3; k++) p[a * 3 + k] = (p[a * 3 + k] + p[b * 3 + k]) / 2;
    for (const c of this.chans) {
      const d = c.buf.data;
      for (let k = 0; k < c.stride; k++) {
        d[a * c.stride + k] = (d[a * c.stride + k] + d[b * c.stride + k]) / 2;
      }
    }
  }

  /** Whether face f still faces roughly the same way with a and b moved to m. */
  private keepsFacing(
    f: number,
    a: number,
    b: number,
    mx: number,
    my: number,
    mz: number,
  ): boolean {
    const o = f * 3, p = this.pos.data, faces = this.faces.data;
    const before = this.triNormal(faces[o], faces[o + 1], faces[o + 2]);
    const q: number[] = [];
    for (let k = 0; k < 3; k++) {
      const v = faces[o + k];
      if (v === a || v === b) q.push(mx, my, mz);
      else q.push(p[v * 3], p[v * 3 + 1], p[v * 3 + 2]);
    }
    const e1x = q[3] - q[0], e1y = q[4] - q[1], e1z = q[5] - q[2];
    const e2x = q[6] - q[0], e2y = q[7] - q[1], e2z = q[8] - q[2];
    const ax = e1y * e2z - e1z * e2y, ay = e1z * e2x - e1x * e2z, az = e1x * e2y - e1y * e2x;
    const len = Math.hypot(before[0], before[1], before[2]) * Math.hypot(ax, ay, az);
    return len > 1e-18 &&
      before[0] * ax + before[1] * ay + before[2] * az >= MIN_COLLAPSE_COS * len;
  }

  private removeFace(f: number): void {
    const o = f * 3, faces = this.faces.data;
    for (let k = 0; k < 3; k++) remove(this.vf(faces[o + k]), f);
    faces[o] = faces[o + 1] = faces[o + 2] = -1;
  }

  private edgeFaces(a: number, b: number): number[] {
    const out: number[] = [];
    for (const f of this.vf(a)) if (this.faceHas(f, b)) out.push(f);
    return out;
  }

  private faceHas(f: number, v: number): boolean {
    const o = f * 3, faces = this.faces.data;
    return faces[o] === v || faces[o + 1] === v || faces[o + 2] === v;
  }

  private isEdgeAt(f: number, i: number, a: number, b: number): boolean {
    const faces = this.faces.data;
    const x = faces[f * 3 + i], y = faces[f * 3 + (i + 1) % 3];
    return (x === a && y === b) || (x === b && y === a);
  }

  private hasDirected(f: number, a: number, b: number): boolean {
    const faces = this.faces.data;
    for (let i = 0; i < 3; i++) {
      if (faces[f * 3 + i] === a && faces[f * 3 + (i + 1) % 3] === b) return true;
    }
    return false;
  }

  private third(f: number, a: number, b: number): number {
    const o = f * 3, faces = this.faces.data;
    for (let k = 0; k < 3; k++) {
      const v = faces[o + k];
      if (v !== a && v !== b) return v;
    }
    return -1;
  }

  /** One-ring of v, written into `out` (reused to avoid allocations). */
  private neighbors(v: number, out: number[]): number[] {
    out.length = 0;
    const faces = this.faces.data;
    for (const f of this.vf(v)) {
      for (let k = 0; k < 3; k++) {
        const w = faces[f * 3 + k];
        if (w !== v && !out.includes(w)) out.push(w);
      }
    }
    return out;
  }

  private triNormal(a: number, b: number, c: number): [number, number, number] {
    const p = this.pos.data;
    const e1x = p[b * 3] - p[a * 3],
      e1y = p[b * 3 + 1] - p[a * 3 + 1],
      e1z = p[b * 3 + 2] - p[a * 3 + 2];
    const e2x = p[c * 3] - p[a * 3],
      e2y = p[c * 3 + 1] - p[a * 3 + 1],
      e2z = p[c * 3 + 2] - p[a * 3 + 2];
    return [e1y * e2z - e1z * e2y, e1z * e2x - e1x * e2z, e1x * e2y - e1y * e2x];
  }

  /** Area-weighted unit normal of v from its current faces. */
  private vertexNormal(v: number): [number, number, number] {
    const faces = this.faces.data;
    let x = 0, y = 0, z = 0;
    for (const f of this.vf(v)) {
      const o = f * 3;
      const n = this.triNormal(faces[o], faces[o + 1], faces[o + 2]);
      x += n[0];
      y += n[1];
      z += n[2];
    }
    const l = Math.hypot(x, y, z) || 1;
    return [x / l, y / l, z / l];
  }
}

function remove(list: number[], value: number): void {
  const i = list.indexOf(value);
  if (i >= 0) list.splice(i, 1);
}
